import { twMerge } from "tailwind-merge";
import { useEffect, useRef, useState } from "react";
import type { Article } from "@core/features/support";
import type { Action } from "@core/features/actions";
import type { Circle } from "@core/features/circle";
import { VideoMeetingActiveTab, VideoMeetingScrollPositions } from "../../videoMeeting.types";
import VideoMeetingChatContainer from "../VideoMeetingChat/VideoMeetingChatContainer";
import VideoMeetingMaterialsContainer from "../VideoMeetingMaterials/VideoMeetingMaterialsContainer";
import VideoMeetingNavigation from "./VideoMeetingNavigation";
import VideoMeetingRosterContainer from "../VideoMeetingRoster/VideoMeetingRosterContainer";
import { useChatStatusWithCircles } from "@platform/hooks/useChatStatus";

type Props = {
  activeTab: VideoMeetingActiveTab;
  scrollPositions: VideoMeetingScrollPositions;
  priorityBasedPolicy: boolean;
  sharingAttendeeId: string;
  onClose: () => void;
  onTabChange: (tab: VideoMeetingActiveTab) => void;
  onScrollPositionSave: (tab: VideoMeetingActiveTab, position: number) => void;
  meetingScriptAction: Action | null;
  unreadCount: number;
  userCircles: Circle[] | null | undefined;
};

export function VideoMeetingSidebar({
  activeTab,
  scrollPositions,
  priorityBasedPolicy,
  sharingAttendeeId,
  onClose,
  onTabChange,
  onScrollPositionSave,
  meetingScriptAction,
  unreadCount = 0,
  userCircles,
}: Props) {
  const { isChatEnabled } = useChatStatusWithCircles({ userCircles });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousActiveTabRef = useRef<VideoMeetingActiveTab>(activeTab);

  // Lazy-mount the chat tab once and keep it mounted for the rest of
  // the meeting. Toggling activeTab between "chat" and other tabs
  // hides/shows the container via CSS instead of unmounting it. Why:
  // every chat unmount destroys the underlying Talk.Chatbox (the
  // <Chatbox> from @talkjs/react calls .destroy() on unmount, then
  // calls session.createChatbox() on next mount — see
  // node_modules/@talkjs/react/dist/talkjs-react.js). That destroy +
  // recreate cycle reloads the iframe and re-syncs the conversation
  // every time the user opens the chat tab. With load-once-per-meeting,
  // the Chatbox loads at most once per meeting session, even across
  // arbitrary tab switches. The Talk.Session itself was always
  // long-lived (mounted at <ChatSession> / <Session> level) — the
  // cost was always at the Chatbox-instance level, not the Session
  // level.
  const [hasOpenedChat, setHasOpenedChat] = useState(false);

  useEffect(() => {
    if (activeTab === "chat") {
      setHasOpenedChat(true);
    }
  }, [activeTab]);

  useEffect(() => {
    const previousTab = previousActiveTabRef.current;
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer && previousTab !== activeTab) {
      scrollContainer.scrollTop = scrollPositions[activeTab];
      previousActiveTabRef.current = activeTab;
    }
  }, [activeTab, scrollPositions]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      onScrollPositionSave(activeTab, scrollContainer.scrollTop);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [activeTab, onScrollPositionSave]);

  return (
    <aside
      className={twMerge(
        "bg-brand-50 rounded-l-md shadow-[0px_0px_24px_0px_#050a00,_0_8px_4px_-2px_#050a00] max-w-[90dvw]",
        "fixed z-30 flex flex-col right-0 top-0 w-96 h-full",
        "sm:relative sm:w-96"
      )}
    >
      <VideoMeetingNavigation
        activeTab={activeTab}
        priorityBasedPolicy={priorityBasedPolicy}
        sharingAttendeeId={sharingAttendeeId}
        onClose={onClose}
        onTabChange={onTabChange}
        unreadCount={unreadCount}
        userCircles={userCircles}
      />
      <div
        ref={scrollContainerRef}
        className="relative flex flex-1 flex-col gap-4 text-brand-800 overflow-y-auto"
      >
        {activeTab === "roster" && <VideoMeetingRosterContainer />}
        {/*
          Chat tab — lazy-mount on first open, keep mounted, toggle
          visibility via Tailwind's `hidden` utility. Do NOT change
          this back to a plain conditional render — that destroys and
          recreates the underlying TalkJS Chatbox iframe on every tab
          switch, which is the slow-load symptom users were observing.
        */}
        {hasOpenedChat && isChatEnabled && (
          <div
            className={
              activeTab === "chat" ? "flex flex-1 flex-col" : "hidden"
            }
          >
            <VideoMeetingChatContainer />
          </div>
        )}
        {activeTab === "materials" && (
          <VideoMeetingMaterialsContainer
            article={
              meetingScriptAction?.target_object_data as unknown as Article
            }
            scrollContainer={scrollContainerRef.current}
          />
        )}
      </div>
    </aside>
  );
}
